/**
 * Date formatting shared by every agency screen (salon cards, members table,
 * member detail, activity timeline). Kept separate from `agency-types.ts` so
 * the display helpers stay small and independently testable.
 */

const RELATIVE_DIVISIONS: [number, Intl.RelativeTimeFormatUnit][] = [
  [60, 'second'],
  [60, 'minute'],
  [24, 'hour'],
  [7, 'day'],
  [4.34524, 'week'],
  [12, 'month'],
  [Number.POSITIVE_INFINITY, 'year'],
];

/**
 * Compact relative phrasing ("3 hours ago", "yesterday", "in 2 days").
 * Returns an em dash for null / unparseable input so callers can render it
 * directly without a fallback branch.
 */
export function formatRelativeDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '—';

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  let duration = (ts - Date.now()) / 1000;
  for (const [amount, unit] of RELATIVE_DIVISIONS) {
    if (Math.abs(duration) < amount) return rtf.format(Math.round(duration), unit);
    duration /= amount;
  }
  return '—';
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Members-table "Last active" phrasing: "Just now", "12 minutes ago",
 * "2 hours ago" (same day), "Yesterday", then a short date ("Sep 24"; the
 * year is added only when it differs from today's). Future timestamps
 * (client clock skew) read as "Just now". Em dash for null / unparseable.
 */
export function formatMemberLastActive(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const ts = date.getTime();
  if (Number.isNaN(ts)) return '—';

  const elapsed = now.getTime() - ts;
  if (elapsed < MINUTE_MS) return 'Just now';
  if (elapsed < HOUR_MS) {
    const minutes = Math.floor(elapsed / MINUTE_MS);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }

  const today = startOfLocalDay(now);
  if (ts >= today) {
    const hours = Math.floor(elapsed / HOUR_MS);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  // Day before today in local time (DST-safe: compare calendar starts, not 24h spans).
  const yesterday = startOfLocalDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  if (ts >= yesterday) return 'Yesterday';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * Local clock time ("4:20 PM"). Newer ICU builds put a narrow no-break space
 * before AM/PM; normalise it so output is stable across Node / browser versions.
 */
export function formatLocalTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\u202f/g, ' ');
}

/** Historical name for {@link formatRelativeDate}; kept so callers need not change. */
export const formatLastActive = formatRelativeDate;

/**
 * Short absolute date ("Sep 14, 2026") for joined / assigned columns.
 * Returns an em dash for null / unparseable input.
 */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Compact day ("Sep 28") for dated rows that sit under a fuller heading —
 * salon-access rows, activity timestamps. The year is added only when it
 * differs from today's. Em dash for null / unparseable input.
 */
export function formatMonthDay(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}
